import { useCallback, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";
import PlayerProfileForm from "../../components/game/PlayerProfileForm";
import {
  isGameAccessUnauthorized,
  loginExistingGamePlayer,
  registerGamePlayer,
} from "../../features/game/gameApi";
import {
  clearGameAccessToken,
  clearGamePlayerOptions,
  getGameAccessToken,
  getGameEntry,
  getGamePlayerOptions,
  isGameAccessTokenValid,
  setGameGuestToken,
  setGamePlayer,
} from "../../features/game/gameSession";

import MrNoty from "@/assets/UI/game/PMR.webp";
import MrsNoty from "@/assets/UI/game/PMRS.webp";

import cls from "./GameProfileSetupPage.module.scss";

const floatSlow = {
  hidden: { opacity: 0.88, scale: 1 },
  show: {
    opacity: [0.88, 1, 0.9],
    scale: [1, 1.1, 1],
    transition: {
      duration: 10,
      ease: "easeInOut",
      repeat: Infinity,
    },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.9, scale: 1.02 },
  show: {
    opacity: [0.92, 1, 0.92],
    scale: [1.1, 1, 1.1],
    x: [0, -2, 0],
    transition: {
      duration: 11.5,
      ease: "easeInOut",
      repeat: Infinity,
    },
  },
};

const slideInUp = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 180,
      damping: 22,
      mass: 0.95,
    },
  },
};

function ExistingPlayerCard({ player, active, onSelect, onJoin, joining }) {
  const avatarSrc = player?.image?.url || "";
  const avatarText =
    player?.image?.sourceType === "upload"
      ? "Uploaded Avatar"
      : player?.image?.avatarKey || "Preset Avatar";

  return (
    <article
      className={`${cls.playerCard} ${active ? cls.playerCardActive : ""}`}
    >
      <button
        type="button"
        className={cls.playerCardSelect}
        onClick={() => onSelect(player._id)}
      >
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt={player?.displayName || avatarText}
            className={cls.playerCardAvatar}
          />
        ) : (
          <div className={cls.playerCardAvatarFallback}>
            {(player?.displayName || "?").slice(0, 1).toUpperCase()}
          </div>
        )}

        <div className={cls.playerCardBody}>
          <div className={cls.playerCardTopRow}>
            <span className={cls.playerCardNumber}>#{player.playerNo}</span>
            <h4>{player.displayName}</h4>
          </div>

          <p>
            {player.partnerStatus} · {player.currentCoins} clothes remain
          </p>
        </div>
      </button>

      <button
        type="button"
        className={cls.joinExistingButton}
        onClick={() => onJoin(player)}
        disabled={joining}
      >
        {joining ? "Joining..." : "Enter lobby as this player"}
      </button>
    </article>
  );
}

export default function GameProfileSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [selectedExistingPlayerId, setSelectedExistingPlayerId] = useState("");

  const game = useMemo(() => getGameEntry(), []);
  const gameAccessToken = useMemo(() => getGameAccessToken(), []);
  const existingPlayers = useMemo(() => getGamePlayerOptions(), []);
  const hasExistingPlayers = existingPlayers.length > 0;
  const registrationClosed = game?.registrationStatus === "closed";
  const hasValidGameAccess = useMemo(
    () => Boolean(game?._id && isGameAccessTokenValid(gameAccessToken)),
    [game?._id, gameAccessToken],
  );

  const finishLobbyEntry = useCallback(
    (data) => {
      setGameGuestToken(data.gameToken);
      setGamePlayer(data.player);
      clearGameAccessToken();
      clearGamePlayerOptions();
      navigate(`/game/${game._id}/lobby`);
    },
    [game?._id, navigate],
  );

  const handleGameAccessExpired = useCallback(() => {
    clearGameAccessToken();
    clearGamePlayerOptions();
    navigate("/game/enter", {
      replace: true,
      state: {
        message:
          "Your game entry session expired before registration finished. Please enter the game again.",
        from: location.pathname,
      },
    });
  }, [location.pathname, navigate]);

  const handleRegister = async (payload) => {
    if (!game?._id || !hasValidGameAccess) {
      handleGameAccessExpired();
      return;
    }

    setLoading(true);
    try {
      const data = await registerGamePlayer(game._id, {
        ...payload,
        gameAccessToken,
      });

      finishLobbyEntry(data);
    } catch (error) {
      if (isGameAccessUnauthorized(error)) {
        handleGameAccessExpired();
        return;
      }

      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.msg ||
          "Could not join the lobby. Please review your profile and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExistingPlayerJoin = async (playerArg) => {
    const playerId = playerArg?._id || selectedExistingPlayerId;
    if (!game?._id || !playerId || !hasValidGameAccess) {
      handleGameAccessExpired();
      return;
    }

    setSelectedExistingPlayerId(playerId);
    setLoading(true);
    try {
      const data = await loginExistingGamePlayer(
        game._id,
        playerId,
        gameAccessToken,
      );
      finishLobbyEntry(data);
    } catch (error) {
      if (isGameAccessUnauthorized(error)) {
        handleGameAccessExpired();
        return;
      }

      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.msg ||
          "Could not enter the lobby with that player profile.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!hasValidGameAccess) {
    return (
      <Navigate
        to="/game/enter"
        replace
        state={{
          message:
            "Your game entry session expired before registration finished. Please enter the game again.",
          from: location.pathname,
        }}
      />
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        <div className={cls.layout}>
          <div className={cls.rightColumn}>
            <section className={cls.searchHero}>
              <div className={cls.searchHeroStage}>
                <Motion.div
                  className={`${cls.heroFrame} ${cls.heroArtFrame}`}
                  variants={slideInUp}
                  initial="hidden"
                  animate="show"
                  aria-hidden="true"
                >
                  <div className={cls.heroArtWrap}>
                    <Motion.img
                      src={MrNoty}
                      alt=""
                      className={`${cls.heroArt} ${cls.heroMr}`}
                      variants={floatSlow}
                      initial="hidden"
                      animate="show"
                      draggable="false"
                    />

                    <Motion.img
                      src={MrsNoty}
                      alt=""
                      className={`${cls.heroArt} ${cls.heroMrs}`}
                      variants={floatSlowAlt}
                      initial="hidden"
                      animate="show"
                      draggable="false"
                    />
                  </div>

                  <div className={cls.heroGlowLeft} />
                  <div className={cls.heroGlowRight} />
                  <div className={cls.heroGlowCenter} />
                </Motion.div>

                <Motion.div
                  className={`${cls.heroFrame} ${cls.heroCard}`}
                  variants={slideInUp}
                  initial="hidden"
                  animate="show"
                >
                  <span className={cls.searchPromptLabel}>PLAYER SETUP</span>

                  <h1 className={cls.searchPromptTitle}>
                    {game?.title || "Game Profile Setup"}
                  </h1>

                  <p className={cls.searchPromptText}>
                    Choose an existing player profile or create a brand-new one
                    before you step into the live lobby.
                  </p>
                </Motion.div>
              </div>
            </section>
            <section className={cls.clubsGridSection}>
              <div className={cls.clubsGridHeader}>
                <h3 className={cls.cityListTitle}>
                  {hasExistingPlayers
                    ? "Create a new player profile"
                    : game?.title || "Game Profile Setup"}
                </h3>
                <p className={cls.cityListMeta}>
                  {registrationClosed
                    ? "Registration is closed. New player setup is hidden, but existing players can still join."
                    : hasExistingPlayers
                      ? "Want a different identity for this game? Create a new profile and enter the lobby with it."
                      : "Set your player details before entering the live game room."}
                </p>
              </div>

              {!registrationClosed ? (
                <div className={cls.clubsGrid}>
                  <Motion.div
                    className={cls.formShell}
                    variants={slideInUp}
                    initial="hidden"
                    animate="show"
                  >
                    <div className={cls.formShellGlow} />
                    <div className={cls.formShellInner}>
                      <PlayerProfileForm
                        onSubmit={handleRegister}
                        loading={loading}
                        gameId={game?._id}
                        gameAccessToken={gameAccessToken}
                        allowUpload={Boolean(game?.allowSelfieUpload)}
                        onGameAccessExpired={handleGameAccessExpired}
                        submitLabel="Create profile and enter lobby"
                      />
                    </div>
                  </Motion.div>
                </div>
              ) : null}
            </section>

            {hasExistingPlayers ? (
              <section className={cls.clubsGridSection}>
                <div className={cls.clubsGridHeader}>
                  <h3 className={cls.cityListTitle}>
                    or Choose an existing player
                  </h3>
                  <p className={cls.cityListMeta}>
                    We found player profiles already created for this game. Pick
                    one to continue to the lobby instantly.
                  </p>
                </div>

                <div className={cls.existingPlayersGrid}>
                  {existingPlayers.map((player) => (
                    <ExistingPlayerCard
                      key={player._id}
                      player={player}
                      active={selectedExistingPlayerId === player._id}
                      onSelect={setSelectedExistingPlayerId}
                      onJoin={handleExistingPlayerJoin}
                      joining={
                        loading && selectedExistingPlayerId === player._id
                      }
                    />
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
